/**
 * KalleHinweis — globales Hinweisband (Meldeweg „kopfzeile“/„mail“, KALLE-07).
 *
 * Steht im Layout ueber dem Seiteninhalt, pollt GET /dashboard/hinweise und zeigt je
 * ungelesenem Hinweis ein schmales Band. Stoppt dauerhaft beim ersten
 * 403 DASHBOARD_NICHT_FREIGESCHALTET. Netzwerkfehler bleiben still (wie MailOutboxWarning).
 * Hinweistexte stammen aus Modellausgabe: nur Textknoten, „Öffnen“ navigiert fest.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellRing } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { ladeHinweise, markiereHinweisGelesen, type HinweisEintrag } from '../pages/dashboard/dashboardApi';
import { HINWEIS_POLL_MS, hinweisText, kuerzeText, sollWeiterPollen } from './kalleHinweisLogik';

const MAX_BAENDER = 3;
const KNOPF =
  'rounded border border-[#1A1A1A]/40 bg-white/60 px-2 py-0.5 text-xs font-medium text-[#1A1A1A] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1A1A]';

export function KalleHinweis() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const [hinweise, setHinweise] = useState<HinweisEintrag[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      setHinweise([]);
      return;
    }
    let abgebrochen = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const laden = async () => {
      try {
        const liste = await ladeHinweise();
        if (!abgebrochen) setHinweise(liste);
      } catch (e) {
        if (!sollWeiterPollen(e)) {
          abgebrochen = true;
          if (timer) clearInterval(timer);
          setHinweise([]);
        }
        // sonst: kein Banner, kein Fehler
      }
    };

    laden();
    timer = setInterval(laden, HINWEIS_POLL_MS);
    return () => {
      abgebrochen = true;
      if (timer) clearInterval(timer);
    };
  }, [isAuthenticated]);

  const gelesen = useCallback(async (h: HinweisEintrag) => {
    setHinweise((alt) => alt.filter((x) => x.id !== h.id));
    try {
      await markiereHinweisGelesen(h.id);
    } catch {
      setHinweise((alt) => (alt.some((x) => x.id === h.id) ? alt : [h, ...alt]));
    }
  }, []);

  if (!isAuthenticated || hinweise.length === 0) return null;

  const sichtbar = hinweise.slice(0, MAX_BAENDER);
  const rest = hinweise.length - sichtbar.length;

  return (
    <div role="status" aria-live="polite" className="pb-3 sm:pb-4 lg:pb-6">
      {/* Abstand unten = Innenabstand des Layouts: das randlose Dashboard zieht sich mit negativen Rändern darunter. */}
      {sichtbar.map((h) => (
        <div
          key={h.id}
          className="mb-2 flex items-center gap-3 rounded border border-[#1A1A1A]/20 bg-[#E9FF57] px-3 py-1.5 text-sm text-[#1A1A1A]"
        >
          <BellRing className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="shrink-0 font-semibold">{kuerzeText(h.titel, 60)}</span>
          <span className="min-w-0 flex-1 truncate">{hinweisText(h.karte)}</span>
          <button type="button" className={KNOPF} onClick={() => navigate('/dashboard?tab=dashboard')}>
            Öffnen
          </button>
          <button type="button" className={KNOPF} onClick={() => gelesen(h)}>
            Gelesen
          </button>
        </div>
      ))}
      {rest > 0 && (
        <div className="mb-2 text-xs text-[#1A1A1A]">+ {rest} weitere — im Dashboard öffnen</div>
      )}
    </div>
  );
}
